import { AxiosError } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { getPublicContracts } from '@/api/contract/getContract';
import BasicButton from '@/components/Common/Button/BasicButton';
import TextInputLabelOff from '@/components/Common/Input/TextInputLabelOff';
import Viewer from '@/components/Contract/Viewer';
import Error from '@/components/Error/Error';
import { DocumentTextSolidIcon, ErrorCircleIcon, XMarkIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { useToastContext } from '@/contexts/Common/ToastContext';

const ContractView = () => {
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToastContext();
  const componentRef = useRef<HTMLDivElement>(null);

  const [pageType, setPageType] = useState('INPUT');
  const [phoneNumberInput, setPhoneNumberInput] = useState('');
  const [isErrorInput, setIsErrorInput] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [contractInfo, setContractInfo] = useState<ContractForm>();
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const onChangeInput = (value: string) => {
    let newValue = value;
    newValue = value.replace(/\D/g, '');

    setPhoneNumberInput(newValue);
  };

  const onClickOk = () => {
    if (pageType === 'VIEW') {
      handleClickDownloadImage();
    } else {
      setPageType('VIEW');
    }
  };

  const handleClickDownloadImage = async () => {
    if (!contractInfo?.fileUrl) return;

    const link = document.createElement('a');
    link.href = contractInfo.fileUrl;
    link.download = `계약서_${contractInfo?.carName || 'image'}.jpeg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const { data, isError, error, isLoading, isFetching } = useQuery(
    ['get-public-contract-detail'],
    () => getPublicContracts(id, phoneNumberInput),
    {
      refetchOnWindowFocus: false,
      enabled: pageType === 'VIEW' && !!id,
      retry: false,
      onSuccess: (res) => {
        setContractInfo(res);
        setIsErrorInput(false);
      },
      onError: (error: AxiosError) => {
        if (error.code === 'CONTRACT_NOT_FOUND') {
          setErrorCode('CONTRACT_NOT_FOUND');
        } else if (error.code === 'CONTRACT_VIEWER_PHONE_NUMBER_NOT_MATCH') {
          setIsErrorInput(true);
          setPageType('INPUT');
        }
      },
    },
  );

  useEffect(() => {
    const handleResize = () => {
      const height = window.visualViewport?.height || window.innerHeight;
      setIsKeyboardOpen(height < window.innerHeight - 150);
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleFocus = () => {
    // phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // 모바일 키보드가 올라온 후 스크롤하기 위해 약간의 지연 추가
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 300);
  };

  if (isFetching || isLoading) {
    return null;
  } else if (errorCode === 'CONTRACT_NOT_FOUND') {
    return (
      <div className="flex flex-col items-center justify-center w-full min-h-[100dvh]">
        <Error
          icon={<ErrorCircleIcon color={colors.gray[5]} width={144} height={144} />}
          title="해당 계약서는</br>삭제된 계약서입니다."
          description=""
        />
      </div>
    );
  } else {
    return (
      <div className="relative flex flex-col justify-between min-h-[100dvh] max-w-[720px] pb-14">
        <div
          className="absolute top-0 right-0 m-4 cursor-pointer"
          onClick={() => navigate('/', { replace: true })}
        >
          <XMarkIcon color={colors.gray[9]}></XMarkIcon>
        </div>
        <div className="p-4">
          {pageType === 'INPUT' && (
            <div className="mt-[80px]">
              <DocumentTextSolidIcon color={colors.gray[4]} width={46} height={46} />
              <div className="font-semibold text-[22px] text-gray-10 mt-[14px]">
                계약서 열람을 위해
                <br />
                휴대폰 번호를 입력해주세요.
              </div>
              <div className="text-gray-7 mt-[10px] mb-[30px] ml-3">
                <div className="relative break-keep">
                  <span className="absolute left-[-12px]">*</span>하이픈(-)을 제외한 숫자만 입력해주세요.
                </div>
                <div>예) 01012345678</div>
              </div>
              <TextInputLabelOff
                ref={phoneInputRef}
                name="phoneNumber"
                placeholder="휴대폰 번호 입력"
                value={phoneNumberInput}
                fontSize={18}
                onChange={(e) => onChangeInput(e.target.value)}
                error={isErrorInput}
                inputMode="numeric"
                errorMsg="휴대폰 번호를 다시 입력해주세요."
                maxLength={11}
                onFocus={handleFocus}
              />
            </div>
          )}
        </div>
        {pageType === 'VIEW' && !isErrorInput && id && (
          <div className="">
            <Viewer ref={componentRef} contractInfo={contractInfo} />
          </div>
        )}

        <div className="flex items-center justify-center fixed w-full mx-auto my-0 px-4 pb-2 bottom-0 left-0 right-0 h-14 max-w-[720px] min-w-[280px] bg-white">
          <div className="absolute top-[-16px] left-0 w-full h-[16px] bg-gradient-to-b from-transparent to-[#ffffff]"></div>
          <BasicButton
            name={pageType === 'INPUT' ? '확인' : '계약서 다운로드'}
            bgColor={colors.blue[8]}
            borderColor={colors.blue[8]}
            textColor={colors.gray[0]}
            fontSize={14}
            height={48}
            fontWeight="bold"
            onClick={() => onClickOk()}
            isDisabled={!phoneNumberInput}
          />
        </div>
      </div>
    );
  }
};

export default ContractView;
